// ============================================
// MEN'S SEXUAL HEALTH QUESTIONNAIRE DATA
// ============================================
// Based on Beluga Health "Mens Sexual Health Questionnaire" (2025).
// Visit Type: mensSexualHealth
// Labs required: none. Follow-up cadence: every 6 months.
// Products: Apomorphine, PT-141, Oxytocin — alone or in combination with a
//           PDE5 inhibitor (sildenafil, tadalafil, etc.).
//
// Color legend (Beluga source of truth):
//   BLACK  — required Q/A questions
//   BLUE   — conditional questions
//   PURPLE — named-field mappings (medicalConditions / selfReportedMeds /
//            allergies / sex); excluded from the Q/A section by the backend
//   RED    — fully disqualifying answers
//   PINK   — product routing (none in this questionnaire)
//
// See docs/BACKEND_MENS_SEXUAL_HEALTH_VISIT_PRD.md for the canonical contract.

// ============================================
// CONSENT BODY (verbatim-faithful from the questionnaire)
// ============================================

const MAIN_CONSENT_BODY = `Consent for PT-141, Oxytocin, Apomorphine, and PDE5 Inhibitors (Men's Libido and Erectile Function)

This consent and product education explains the use of compounded PT-141 (bremelanotide), oxytocin, and apomorphine, as well as phosphodiesterase type 5 inhibitors (PDE5i). Limited data exist regarding the safety and effectiveness of PT-141, oxytocin, and apomorphine in men, and standardized dosing guidelines have not been established.

Purpose and Benefits

PT-141 (Bremelanotide): stimulates melanocortin receptors in the brain, which may improve erectile function, reliability of erections, and sexual satisfaction.
Oxytocin: often called the "bonding hormone," it may promote intimacy, trust, and sexual desire.
Apomorphine: a dopamine agonist that may improve erectile response by acting on central nervous system pathways; may be beneficial in men who do not respond adequately to PDE5 inhibitors.
PDE5 Inhibitors (Sildenafil, Tadalafil, Vardenafil, Avanafil): improve erectile function by increasing blood flow to the penis during sexual stimulation.

Together, these treatments may address decreased libido, support erectile function, and improve sexual health and well-being.

Potential Risks and Side Effects
- PT-141: nausea or upset stomach, headache, flushing or redness, dizziness or lightheadedness, injection-site reactions (if injectable), possible skin hyperpigmentation.
- Oxytocin: mild headache, nausea, temporary changes in blood pressure, mood or emotional fluctuations.
- Apomorphine: nausea and vomiting, low blood pressure/fainting/dizziness, sleepiness or yawning, rarely changes in heart rhythm.
- PDE5 inhibitors: headache, flushing, indigestion, nasal congestion, dizziness, blurred vision or color changes, back pain or muscle aches; rare: hearing loss, ringing in ears, priapism (erection >4 hours), allergic reactions.

Rare but Serious Risks (all): significant or sustained blood pressure changes (which may increase risk of stroke or heart attack), severe allergic reactions (swelling of face, lips, tongue, or throat), noticeable changes in mood or behavior.

Do not use PDE5 inhibitors if: you take nitrates for chest pain (angina), you use recreational "poppers" (amyl nitrate or nitrite), or you have severe heart or liver problems, very low blood pressure, or recent stroke/heart attack.

Seek emergency care immediately if you experience chest pain, trouble breathing, extreme dizziness, a prolonged erection, or signs of a severe allergic reaction.

Monitoring Plan: attend all scheduled follow-up visits, complete any recommended labs or blood pressure checks, and report any new or worsening symptoms (especially cardiovascular, mood, or visual changes).

Alternative Treatments: lifestyle modifications (exercise, weight loss, improved sleep, stress reduction, limiting alcohol/smoking); sex therapy, counseling, or relationship therapy; other medical options (hormone therapy, vacuum erection devices, penile injections, suppositories, or surgical implants).

Your Responsibilities: use medications exactly as prescribed; do not combine with contraindicated medications (especially nitrates or "poppers"); report all other medications, supplements, or health changes; immediately report concerning side effects.

By selecting below, you confirm that you have read and understood this information, are aware of the potential benefits, risks, and alternatives, understand that use of PT-141, oxytocin, and apomorphine is off-label and not FDA-approved for men, agree to follow your provider's instructions and attend all follow-ups, and understand you may withdraw from treatment at any time.`;

// ============================================
// PURPLE — named-field questions (mapped server-side, excluded from Q/A)
// ============================================

const medicalConditionsQuestion = {
  id: 'medical-conditions',
  type: 'textarea',
  question: 'Please identify all your current medical conditions',
  subtitle: 'Include chronic conditions, past surgeries, and any other relevant health issues.',
  placeholder: 'e.g., Hypertension, Diabetes, Previous surgery, etc.',
};

const selfReportedMedsQuestion = {
  id: 'self-reported-meds',
  type: 'textarea',
  question: 'Please list all your current medications including dosages',
  placeholder: 'e.g., Lisinopril 10mg daily, Atorvastatin 20mg daily…',
};

const allergiesQuestion = {
  id: 'allergies',
  type: 'textarea',
  question: 'Please list all of your known allergies',
  placeholder: 'e.g., Penicillin (rash), Shellfish (anaphylaxis)…',
};

const sexAtBirthQuestion = {
  id: 'sex-at-birth',
  type: 'single-select',
  question: 'What was your sex assigned at birth?',
  subtitle: 'This intake supports treatment for individuals assigned male at birth.',
  options: [
    { label: 'Male', value: 'male' },
    { label: 'Female', value: 'female', disqualifies: true },
  ],
};

// ---------- Vitals ----------

const heightWeightQuestion = {
  id: 'height-weight',
  type: 'height-weight-bmi',
  question: 'What is your height and weight?',
  subtitle: 'Your provider uses this to help personalize care.',
  skipBmiCheck: true,
};

// ---------- Sexual health concerns ----------

const sexualHealthConcernsQuestion = {
  id: 'sexual-health-concerns',
  type: 'multi-select',
  question: 'What are your primary concerns related to your sexual health?',
  subtitle: 'Select all that apply.',
  options: [
    { label: 'Difficulty obtaining or maintaining an erection', value: 'erectile-difficulty' },
    { label: 'Decreased sexual desire', value: 'decreased-desire' },
    { label: 'Difficulty becoming aroused', value: 'difficulty-arousal' },
    { label: 'Difficulty achieving orgasm', value: 'difficulty-orgasm' },
    { label: 'Other', value: 'other' },
  ],
};

const sexualHealthSymptomsDetailsQuestion = {
  id: 'sexual-health-symptoms-details',
  type: 'textarea',
  question: 'Tell us more about your other sexual health symptom(s)',
  placeholder: 'Describe what you have been experiencing…',
};

const concernDurationQuestion = {
  id: 'concern-duration',
  type: 'single-select',
  question: 'How long have you been experiencing these concerns?',
  options: [
    { label: 'Less than 3 months', value: 'less-than-3-months' },
    { label: '3 to 6 months', value: '3-to-6-months' },
    { label: 'More than 6 months', value: 'more-than-6-months' },
  ],
};

const distressQuestion = {
  id: 'distress',
  type: 'single-select',
  question: 'Do these concerns cause you distress or affect your relationships?',
  options: [
    { label: 'Yes', value: 'yes' },
    { label: 'No', value: 'no' },
  ],
};

// ---------- History / cardiovascular screening ----------

const physicalExamQuestion = {
  id: 'physical-exam',
  type: 'single-select',
  question: 'Have you had a physical exam with a healthcare provider in the past 5 years?',
  options: [
    { label: 'Yes, I am in good health', value: 'yes-good-health' },
    { label: 'Yes, but they found problems', value: 'yes-found-problems' },
    { label: 'No', value: 'no' },
  ],
};

const physicalExamDetailsQuestion = {
  id: 'physical-exam-details',
  type: 'textarea',
  question: 'Please explain any issues during your last physical exam',
  placeholder: 'Describe what was found…',
};

const bpTakenQuestion = {
  id: 'bp-taken',
  type: 'single-select',
  question: 'Was your blood pressure taken in the past year?',
  options: [
    { label: 'Yes', value: 'yes' },
    { label: 'No, I will get a new blood pressure measurement', value: 'no-will-measure' },
  ],
};

const bpValueQuestion = {
  id: 'bp-value',
  type: 'text',
  question: 'What was the value?',
  subtitle: 'Example: 115/72',
  placeholder: 'e.g., 115/72',
};

const nitrateMedsQuestion = {
  id: 'nitrate-meds',
  type: 'multi-select',
  question: 'Do you take any of the following medicines?',
  subtitle: 'Select all that apply.',
  options: [
    { label: 'Nitroglycerin spray, ointment, patches or tablets', value: 'nitroglycerin', disqualifies: true },
    { label: 'Isosorbide mononitrate, or isosorbide dinitrate (Isordil, Dilatrate, Sorbitrate, Imdur, Ismo, Monoket)', value: 'isosorbide', disqualifies: true },
    { label: 'Other medicines containing nitrates', value: 'other-nitrates', disqualifies: true },
    { label: 'Alpha blocker medications: doxazosin (Cardura), prazosin (Minipress), terazosin (Hytrin)', value: 'alpha-blockers', disqualifies: true },
    { label: 'Riociguat (Adempas)', value: 'riociguat', disqualifies: true },
    { label: 'None apply', value: 'none' },
  ],
};

const cardiovascularSymptomsQuestion = {
  id: 'cardiovascular-symptoms',
  type: 'multi-select',
  question: 'Do you experience any of the following cardiovascular symptoms?',
  subtitle: 'Select all that apply.',
  options: [
    { label: 'Chest pain or shortness of breath when climbing 2 flights of stairs or walking 4 blocks', value: 'chest-pain-exertion', disqualifies: true },
    { label: 'Chest pain or shortness of breath with sexual activity', value: 'chest-pain-sex', disqualifies: true },
    { label: 'Unexplained fainting or dizziness', value: 'fainting-dizziness', disqualifies: true },
    { label: 'Prolonged cramping of the legs with exercise', value: 'leg-cramping', disqualifies: true },
    { label: 'Abnormal heart beats or rhythms', value: 'arrhythmia', disqualifies: true },
    { label: 'None of these apply to me', value: 'none' },
  ],
};

const conditionsQuestion = {
  id: 'conditions',
  type: 'multi-select',
  question: 'Do you have now, or have you ever had any of the following conditions?',
  subtitle: 'Select all that apply.',
  options: [
    { label: 'Priapism (erection lasting longer than 4 hours)', value: 'priapism', disqualifies: true },
    { label: 'Low blood pressure', value: 'low-bp', disqualifies: true },
    { label: 'Difficult to control blood pressure', value: 'difficult-bp', disqualifies: true },
    { label: 'History of stroke', value: 'stroke', disqualifies: true },
    { label: 'A prior heart attack or heart failure', value: 'heart-attack-failure' },
    { label: 'Peripheral artery disease', value: 'peripheral-artery-disease', disqualifies: true },
    { label: 'Idiopathic Hypertrophic Subaortic Stenosis', value: 'ihss', disqualifies: true },
    { label: 'Any areas of your skin that appear darker or discolored compared to your normal skin tone (hyperpigmentation)', value: 'hyperpigmentation', disqualifies: true },
    { label: 'None apply', value: 'none' },
  ],
};

const heartAttackRecentQuestion = {
  id: 'heart-attack-recent',
  type: 'single-select',
  question: 'Have you had a heart attack in the last six months?',
  options: [
    { label: 'Yes', value: 'yes', disqualifies: true },
    { label: 'No', value: 'no' },
  ],
};

const conditionsDetailsQuestion = {
  id: 'conditions-details',
  type: 'textarea',
  question: 'Please tell us more about the conditions you selected',
  placeholder: 'Tell us more…',
};

const peyroniesQuestion = {
  id: 'peyronies',
  type: 'single-select',
  question: "Do you have a marked curve or bend in the penis that interferes with sex, or Peyronie's Disease?",
  options: [
    { label: 'Yes', value: 'yes' },
    { label: 'No', value: 'no' },
  ],
};

const penileBendingRecentQuestion = {
  id: 'penile-bending-recent',
  type: 'single-select',
  question: 'Have you had active bending of your penis within the last 12 months?',
  options: [
    { label: 'Yes', value: 'yes', disqualifies: true },
    { label: 'No', value: 'no' },
  ],
};

const painErectionsQuestion = {
  id: 'pain-erections',
  type: 'single-select',
  question: 'Do you experience pain with erections or with ejaculation?',
  options: [
    { label: 'Yes', value: 'yes', disqualifies: true },
    { label: 'No', value: 'no' },
  ],
};

const tightForeskinQuestion = {
  id: 'tight-foreskin',
  type: 'single-select',
  question: 'Do you have a foreskin that is too tight?',
  options: [
    { label: 'Yes', value: 'yes', disqualifies: true },
    { label: 'No', value: 'no' },
  ],
};

const additionalQuestionsQuestion = {
  id: 'additional-questions',
  type: 'textarea',
  question: 'What other information or questions do you have for the doctor?',
  subtitle: 'Feel free to share any concerns or questions.',
  placeholder: 'Type your questions or concerns here…',
};

// ---------- Contact / shipping / identity (FE commerce + delivery) ----------

const basicContactQuestion = {
  id: 'basic-contact',
  type: 'basic-contact',
  question: 'How can we reach you?',
  subtitle: "We'll use this to send updates about your treatment.",
};

const shippingAddressStep = {
  id: 'shipping-address',
  type: 'shipping-address',
  question: 'Shipping Address',
};

const identityVerificationStep = {
  id: 'identity-verification',
  type: 'file-upload',
  question: 'Identity Verification',
  subtitle:
    "Upload a photo of a valid government-issued photo ID such as a driver's license or passport which has your picture, name and date of birth clearly visible. If applicable, upload a photo of the front and the back especially when using a military ID as your date of birth is on the back of the card. Make sure the photo is clear and legible.",
  // Required by Beluga — ID photo cannot be skipped (2026-06-27).
};

const mainConsentQuestion = {
  id: 'main-consent',
  type: 'consent-dropdown',
  question: 'PT-141, Oxytocin, Apomorphine, and PDE5 Inhibitors — Informed Consent',
  consentText: MAIN_CONSENT_BODY,
  options: [
    { label: 'I have read the above information, I understand the risks, and I would like to proceed.', value: 'consent' },
    { label: 'I have read the above information, and I do NOT wish to proceed.', value: 'decline', disqualifies: true },
  ],
};

const checkoutStep = {
  id: 'checkout',
  type: 'checkout',
  question: 'Select Your Treatment Plan',
};

const completeStep = {
  id: 'complete',
  type: 'complete',
  question: 'Complete',
};

// ============================================
// FULL FLOW (canonical questionnaire order)
// ============================================

export const mensSexualHealthFlow = [
  // PURPLE / named-field
  medicalConditionsQuestion,
  selfReportedMedsQuestion,
  allergiesQuestion,
  sexAtBirthQuestion,

  heightWeightQuestion,

  // Sexual health concerns
  sexualHealthConcernsQuestion,
  sexualHealthSymptomsDetailsQuestion, // conditional: concerns includes 'other'
  concernDurationQuestion,
  distressQuestion,

  // History / cardiovascular screening
  physicalExamQuestion,
  physicalExamDetailsQuestion, // conditional: physical-exam === 'yes-found-problems'
  bpTakenQuestion,
  bpValueQuestion, // conditional: bp-taken === 'yes'
  nitrateMedsQuestion,
  cardiovascularSymptomsQuestion,

  // Conditions + heart-attack branch
  conditionsQuestion,
  heartAttackRecentQuestion, // conditional: conditions includes 'heart-attack-failure'
  conditionsDetailsQuestion, // conditional: conditions includes 'heart-attack-failure'

  // Anatomical screening
  peyroniesQuestion,
  penileBendingRecentQuestion, // conditional: peyronies === 'yes'
  painErectionsQuestion,
  tightForeskinQuestion,

  additionalQuestionsQuestion,

  // Contact / shipping / identity
  basicContactQuestion,
  shippingAddressStep,
  identityVerificationStep,

  mainConsentQuestion,
  checkoutStep,
  completeStep,
];

export const mensSexualHealthFlowById = mensSexualHealthFlow.reduce((acc, slide) => {
  acc[slide.id] = slide;
  return acc;
}, {});

// ============================================
// Dynamic flow builder — strips conditionals that have not fired
// ============================================

export const buildDynamicMensSexualHealthFlow = (answers) => {
  let flow = [...mensSexualHealthFlow];
  const arr = (id) => (Array.isArray(answers[id]) ? answers[id] : []);

  // sexual-health-symptoms-details — only when 'other' selected
  if (!arr('sexual-health-concerns').includes('other')) {
    flow = flow.filter((q) => q.id !== 'sexual-health-symptoms-details');
  }

  // physical-exam-details — only when they found problems
  if (answers['physical-exam'] !== 'yes-found-problems') {
    flow = flow.filter((q) => q.id !== 'physical-exam-details');
  }

  // bp-value — only when BP was taken in the past year
  if (answers['bp-taken'] !== 'yes') {
    flow = flow.filter((q) => q.id !== 'bp-value');
  }

  // heart-attack-recent + conditions-details — only when prior heart attack/failure selected
  if (!arr('conditions').includes('heart-attack-failure')) {
    flow = flow.filter((q) => !['heart-attack-recent', 'conditions-details'].includes(q.id));
  }

  // penile-bending-recent — only when Peyronie's = yes
  if (answers['peyronies'] !== 'yes') {
    flow = flow.filter((q) => q.id !== 'penile-bending-recent');
  }

  return flow;
};

// ============================================
// Per-answer disqualifier resolver
// ============================================

const MULTI_SELECT_DISQUALIFIERS = {
  'nitrate-meds': ['nitroglycerin', 'isosorbide', 'other-nitrates', 'alpha-blockers', 'riociguat'],
  'cardiovascular-symptoms': ['chest-pain-exertion', 'chest-pain-sex', 'fainting-dizziness', 'leg-cramping', 'arrhythmia'],
  conditions: ['priapism', 'low-bp', 'difficult-bp', 'stroke', 'peripheral-artery-disease', 'ihss', 'hyperpigmentation'],
};

const DISQUALIFY_REASONS = {
  'sex-at-birth':
    'This program supports treatment for individuals assigned male at birth. We cannot continue with this submission.',
  'nitrate-meds':
    'These medications cannot be combined safely with nitrates, alpha blockers, or riociguat. We cannot proceed for your safety.',
  'cardiovascular-symptoms':
    'Your reported cardiovascular symptoms need to be evaluated in person before these medications can be considered. Please seek care with your provider.',
  conditions:
    'Based on your medical history, these medications are not safe for you right now. Please discuss alternatives with your primary care provider.',
  'heart-attack-recent':
    'Because of a recent heart attack, these medications are not safe for you right now. Please follow up with your provider.',
  'penile-bending-recent':
    'Active penile bending needs to be evaluated by a provider before treatment. We cannot proceed right now.',
  'pain-erections':
    'Pain with erections or ejaculation should be evaluated by a provider before treatment. We cannot proceed right now.',
  'tight-foreskin':
    'This finding should be evaluated by a provider before treatment. We cannot proceed right now.',
  'main-consent':
    'You have indicated that you do not wish to continue with this treatment.',
};

export const isDisqualifyingAnswer = (questionId, answer) => {
  // Single-select hard rejects.
  const singleRejects = {
    'sex-at-birth': 'female',
    'heart-attack-recent': 'yes',
    'penile-bending-recent': 'yes',
    'pain-erections': 'yes',
    'tight-foreskin': 'yes',
    'main-consent': 'decline',
  };
  if (singleRejects[questionId] !== undefined && answer === singleRejects[questionId]) {
    return { disqualified: true, reason: DISQUALIFY_REASONS[questionId] };
  }

  // Multi-select hard rejects — any disqualifying option selected.
  if (Array.isArray(answer) && MULTI_SELECT_DISQUALIFIERS[questionId]) {
    const hit = MULTI_SELECT_DISQUALIFIERS[questionId].some((v) => answer.includes(v));
    if (hit) {
      return { disqualified: true, reason: DISQUALIFY_REASONS[questionId] };
    }
  }

  return { disqualified: false };
};

// ============================================
// TREATMENT PLANS (placeholder pricing — backend reconciles SKU + medId on
// submission, same pattern as the WSH/TRT flows). The FE renders these so
// checkout works without an extra round-trip. No questionnaire-driven product
// routing in this flow (no PINK rules). Which of these Beluga can actually
// fill is a backend seed concern — see the PRD (likely a catalog gap as with
// the women's flow).
// ============================================

// The product MSH checkout starts on: sildenafil tablets are the standard
// first-line ED therapy, so the patient lands with it already selected (and can
// deselect / add others). Keep in sync with the `recommended: true` plan below —
// the slug is what the checkout pre-selects and what submit relays.
export const MSH_DEFAULT_PLAN_SLUG = 'msh-pde5i-sildenafil-monthly';

// §default product — the LIVE MSH checkout is catalog-driven (CatalogCheckoutStep
// over GET /catalog/medications?visitType=mensSexualHealth), so the default the
// patient actually sees is whichever SKU this picks, not the hardcoded plan list
// above. Without it the step defaults to the cheapest SKU, which today is plain
// oxytocin — an ED patient should land on sildenafil.
//
// Preference: sildenafil TABLETS → any sildenafil SKU → any PDE5i (tadalafil) →
// null (caller falls back to cheapest). NOTE: as of 2026-08-11 no standalone
// sildenafil tablet exists in either pharmacy's catalog — every sildenafil SKU is
// an oxytocin/apomorphine combination troche/film — so tier 1 matches nothing and
// this lands on tier 2. It starts picking real tablets the moment ops seeds them.
// See docs/BACKEND_MSH_PDE5I_CATALOG_GAP_PRD.md.
export function pickMshRecommendedSku(items) {
  if (!Array.isArray(items) || !items.length) return null;
  const hay = (m) => `${m?.name || ''} ${m?.medication || ''} ${m?.strength || ''} ${m?.dose || ''}`;
  const cheapest = (list) =>
    list.slice().sort((a, b) => (Number(a.price) || 0) - (Number(b.price) || 0))[0] || null;
  const matching = (re) => cheapest(items.filter((m) => re.test(hay(m))));
  return (
    matching(/sildenafil[\s\S]*\b(tab|tabs|tablet|tablets)\b/i) ||
    matching(/sildenafil/i) ||
    matching(/tadalafil/i) ||
    null
  );
}

export const mensSexualHealthPlans = [
  {
    category: 'PDE5 Inhibitor',
    description: 'Oral therapy to support erectile function',
    productKey: 'pde5i',
    plans: [
      {
        plan_slug: 'msh-pde5i-sildenafil-monthly',
        plan_name: 'Sildenafil Tablets — Monthly',
        plan_price: 89,
        plan_content:
          'Sildenafil tablets,On-demand oral therapy,Monthly supply,Provider consultation,Free shipping',
        // The default/recommended MSH product — see MSH_DEFAULT_PLAN_SLUG.
        recommended: true,
      },
      {
        plan_slug: 'msh-pde5i-tadalafil-monthly',
        plan_name: 'Tadalafil — Monthly',
        plan_price: 99,
        plan_content:
          'Compounded tadalafil,Daily or on-demand oral therapy,Monthly supply,Provider consultation,Free shipping',
      },
    ],
  },
  {
    category: 'PT-141 (Bremelanotide)',
    description: 'For desire and erectile support',
    productKey: 'pt141',
    plans: [
      {
        plan_slug: 'msh-pt141-monthly',
        plan_name: 'PT-141 — Monthly',
        plan_price: 149,
        plan_content:
          'Compounded PT-141 (Bremelanotide),Monthly supply,Provider consultation,Free shipping',
      },
    ],
  },
  {
    category: 'Oxytocin',
    description: 'Compounded therapy to support intimacy and desire',
    productKey: 'oxytocin',
    plans: [
      {
        plan_slug: 'msh-oxytocin-monthly',
        plan_name: 'Oxytocin — Monthly',
        plan_price: 99,
        plan_content:
          'Compounded oxytocin,Monthly supply,Provider consultation,Free shipping',
      },
    ],
  },
  {
    category: 'Apomorphine',
    description: 'For men who do not respond adequately to PDE5 inhibitors',
    productKey: 'apomorphine',
    plans: [
      {
        plan_slug: 'msh-apomorphine-monthly',
        plan_name: 'Apomorphine — Monthly',
        plan_price: 129,
        plan_content:
          'Compounded apomorphine,Monthly supply,Provider consultation,Free shipping',
      },
    ],
  },
];

export default mensSexualHealthFlow;
